import { formatDateTime, formatFileSize, formatPrice, formatStringWithUnderscores } from 'lib/utils/formatters';
import { useState } from 'react';
import { Card, CardHeader, CardBody, Collapse, Button, Row, Col, Spinner } from 'reactstrap';
import profile_placeholder from "assets/images/profile_placeholder.png";
import AssignInspectorModal from 'pages/ListingDetail/AssignInspectorModal';
import TransactionDetailsModal from 'pages/ListingDetail/TransactionDetailsModal';
import UpdatesRequiredModal from 'pages/ListingDetail/UpdatesRequiredModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSecondaryInspectionReportStatus } from 'endpoints/builder/builderPropertyListingDetails';
import { toast } from 'react-toastify';
// import AssignInspectorModal from './AssignInspectorModal';

interface SecondaryInspectionDetailCardProps {
    secondaryInspectionData: any;
    setSecondaryInspectionId: (id: string) => void;
    secondaryInspectionId?: string;
}

const SecondaryInspectionDetailCard = ({ secondaryInspectionData, setSecondaryInspectionId, secondaryInspectionId }: SecondaryInspectionDetailCardProps) => {
    const [isOpen, setIsOpen] = useState(true); // Changed from false to true
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false); // State for AssignInspectorModal
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [updatesModalOpen, setUpdatesModalOpen] = useState(false);

    const queryClient = useQueryClient();

    // Function to get badge color based on status
    const getStatusBadgeColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'bg-warning';
            case 'assigned':
            case 'approved':
            case 'accepted':
            case 'scheduled':
                return 'bg-success';
            case 'rejected':
            case 'call_to_action':
                return 'bg-danger';
            case 'completed':
                return 'bg-info';
            default:
                return 'bg-secondary';
        }
    };

    // Add mutation for call to action following PropertyDocuments pattern
    const updateReportMutation = useMutation({
        mutationFn: (data: {
            inspectionReportId: string;
            rejectionReason?: string;
            status: string;
        }) => updateSecondaryInspectionReportStatus(data),
        onSuccess: (response) => {
            if (response?.status_code === 200) {
                toast.success(response?.response || 'Inspection report status updated successfully');
                // Invalidate queries to refresh data
                queryClient.invalidateQueries({ queryKey: ['propertyUnitSecondaryInspectionDetails'] });
            } else {
                toast.error('Failed to send call to action');
            }
        },
        onError: (error: any) => {
            console.error('Error updating inspection report status:', error);
            toast.error(error?.response?.data?.message || 'Failed to send call to action');
        }
    });

    const toggle = () => setIsOpen(!isOpen);
    const toggleAssignModal = () => setIsAssignModalOpen(!isAssignModalOpen);

    // const [callToAction, setCallToAction] = useState(false);

    // Add download handler function
    const handleDownloadReport = () => {
        if (secondaryInspectionData?.SecondaryInspectionReport?.reportFileUrl) {
            window.open(secondaryInspectionData.SecondaryInspectionReport.reportFileUrl, '_blank');
        }
    };

    // Handle call to action confirmation following PropertyDocuments pattern
    const handleCallToActionConfirm = async (finalReason: string) => {
        if (!secondaryInspectionData?.SecondaryInspectionReport?.id || !finalReason.trim()) {
            toast.error('Rejection reason is required');
            return;
        }

        // Close modal first
        setUpdatesModalOpen(false);

        // Process the call to action with reason
        try {
            await updateReportMutation.mutateAsync({
                inspectionReportId: secondaryInspectionData?.SecondaryInspectionReport?.id,
                rejectionReason: finalReason,
                status: 'call_to_action'
            });
        } catch (error) {
            // Error is handled in mutation onError
        }
    };

    // Handle modal cancellation following PropertyDocuments pattern  
    const handleCallToActionCancel = () => {
        // Simply close modal without any status change
        setUpdatesModalOpen(false);
        // No API call, no status change - everything remains as it was
    };

    // Sanitize phone for tel: links (keep leading + if present)
    const sanitizePhoneForTel = (raw?: string) => {
        if (!raw) return '';
        const trimmed = String(raw).trim();
        const hasPlus = trimmed.startsWith('+');
        const digitsOnly = trimmed.replace(/[^\d]/g, '');
        return hasPlus ? `+${digitsOnly}` : digitsOnly;
    };

    return (
        <Card className="mb-1 border rounded-0 shadow-none">
            <CardHeader onClick={toggle} style={{ cursor: 'pointer' }} className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Secondary Inspection</h6>
                <div className="d-flex align-items-center">
                    <span className={`badge ${getStatusBadgeColor(secondaryInspectionData?.status)} text-light me-2`}>{formatStringWithUnderscores(secondaryInspectionData?.status) || 'N/A'}</span>
                    <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} style={{ fontSize: '1.25rem' }}></i>
                </div>
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    <Row className="mb-2">
                        <Col xs={4}>Status</Col>
                        <Col xs={8} className="text-end">
                            <span className={`badge ${getStatusBadgeColor(secondaryInspectionData?.status)} text-light`}>{formatStringWithUnderscores(secondaryInspectionData?.status) || 'N/A'}</span>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col xs={4}>Date</Col>
                        <Col xs={8} className="text-end">{formatDateTime(secondaryInspectionData?.createdAt) || 'N/A'}</Col>
                    </Row>
                    <Row className="mb-2">
                        <Col xs={4}>Schedule Vist Date</Col>
                        <Col xs={8} className="text-end">{formatDateTime(secondaryInspectionData?.scheduledVisits?.scheduled_date) || 'N/A'}</Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={4}>Payment</Col>
                        <Col xs={8} className="text-end">
                            {`$${formatPrice(secondaryInspectionData?.inspection_amount)}`}
                        </Col>
                    </Row>
                    {/* Inspector Box */}
                    <div className="mb-3">
                        {secondaryInspectionData?.inspector && <div className="mb-3">
                            <h6 className="mb-2">Inspector</h6>
                            <div className="border mb-0 d-flex align-items-center p-2" style={{ minHeight: '56px' }}>
                                <div className="rounded-circle bg-light d-flex justify-content-center align-items-center" style={{ width: '40px', height: '40px' }}>
                                    <img src={secondaryInspectionData?.inspector?.profile_image || profile_placeholder} alt='Inspector Profile' className="rounded-circle" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <span className="ms-2 flex-grow-1" style={{ fontWeight: 500 }}>{`${secondaryInspectionData?.inspector?.first_name} ${secondaryInspectionData?.inspector?.last_name || 'N/A'}`}</span>
                                {secondaryInspectionData?.inspector?.phone_no && (
                                    <a
                                        href={`tel:${sanitizePhoneForTel(secondaryInspectionData?.inspector?.phone_no)}`}
                                        className="d-inline-flex align-items-center justify-content-center"
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            background: '#f3f4f6',
                                            textDecoration: 'none'
                                        }}
                                        aria-label={`Call ${secondaryInspectionData?.inspector?.first_name || ''} ${secondaryInspectionData?.inspector?.last_name || ''}`}
                                        title={`Call ${secondaryInspectionData?.inspector?.first_name || ''} ${secondaryInspectionData?.inspector?.last_name || ''} (${secondaryInspectionData?.inspector?.phone_no})`}
                                    >
                                        <i className="ri-phone-line" style={{ fontSize: '1.1rem', color: '#111' }}></i>
                                    </a>
                                )}
                            </div>
                        </div>}
                        {(secondaryInspectionData?.scheduledVisits?.scheduled_date === undefined && !secondaryInspectionData?.SecondaryInspectionReport) && (<Button className="w-100 rounded-0" color="success" style={{ fontWeight: 500 }} onClick={toggleAssignModal}>
                            {secondaryInspectionData?.inspector ? 'Change Inspector' : 'Assign Inspector'}
                        </Button>)}
                    </div>
                    {/* Inspection Report Section */}
                    <div className="mb-3">
                        {secondaryInspectionData?.SecondaryInspectionReport?.reportFileUrl &&
                            <div>
                                <h6 className="mb-3">Inspection Report</h6>
                                <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle bg-light d-flex justify-content-center align-items-center me-3" style={{ width: '48px', height: '48px' }}>
                                        <i className="ri-file-text-line" style={{ fontSize: '2rem', color: '#fa7575ff' }}></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div >Inspection Report</div>
                                        {secondaryInspectionData?.SecondaryInspectionReport?.file_size && (
                                            <div >{formatFileSize(secondaryInspectionData?.SecondaryInspectionReport?.file_size)}</div>
                                        )}
                                    </div>
                                    <div
                                        onClick={handleDownloadReport}
                                        style={{ cursor: 'pointer' }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    >
                                        <i className="ri-download-cloud-line" style={{ fontSize: '1.5rem', color: '#222', cursor: 'pointer' }}></i>
                                    </div>
                                </div>

                                {/* Report Created Date */}
                                {secondaryInspectionData?.SecondaryInspectionReport?.createdAt && (
                                    <div className="d-flex align-items-center justify-content-center mb-3 p-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '8px',
                                            fontSize: '0.875rem'
                                        }}>
                                        <i className="ri-calendar-line me-2" style={{ color: '#6c757d', fontSize: '1rem' }}></i>
                                        <span style={{ color: '#495057', fontWeight: '500' }}>
                                            Report Created: {formatDateTime(secondaryInspectionData?.SecondaryInspectionReport?.createdAt)}
                                        </span>
                                    </div>
                                )}
                            </div>}
                        {/* Call to Action */}
                        {secondaryInspectionData?.SecondaryInspectionReport?.rejectionReason && <div
                            className="d-flex flex-column p-3 rounded mb-3"
                            style={{ background: '#f7f8fa' }}
                        >
                            <div className="d-flex align-items-center mb-1">
                                <div
                                    className="rounded-circle d-flex justify-content-center align-items-center me-2"
                                    style={{
                                        width: 28,
                                        height: 28,
                                        background: '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                    }}
                                >
                                    <i className="ri-error-warning-line" style={{ fontSize: 18, color: '#e80e0eff' }}></i>
                                </div>
                                <span className="fw-bold" style={{ fontSize: 18 }}>Call to Action</span>
                            </div>
                            <div style={{ fontSize: 17, lineHeight: 1.3 }}>
                                {secondaryInspectionData?.SecondaryInspectionReport?.rejectionReason}
                            </div>
                        </div>}

                        {/* <h6 className="mb-3">Updated Report</h6>

                        <div className="d-flex align-items-center mb-3">
                            <div className="rounded-circle bg-light d-flex justify-content-center align-items-center me-3" style={{ width: '48px', height: '48px' }}>
                                <i className="ri-file-text-line" style={{ fontSize: '2rem', color: '#222' }}></i>
                            </div>
                            <div className="flex-grow-1">
                                <div >Inspection Report</div>
                                <div >123 KBs</div>
                            </div>
                            <div>
                                <i className="ri-download-cloud-line" style={{ fontSize: '1.5rem', color: '#222', cursor: 'pointer' }}></i>
                            </div>
                        </div> */}

                        {secondaryInspectionData?.SecondaryInspectionReport?.reportFileUrl && secondaryInspectionData?.SecondaryInspectionReport?.status === 'pending' && <Button className="w-100 rounded-0 mb-2" color="success" style={{ fontWeight: 500 }} onClick={() => { updateReportMutation.mutate({ inspectionReportId: secondaryInspectionData?.SecondaryInspectionReport?.id, status: 'approved' }); }} disabled={updateReportMutation.isPending}>
                            {updateReportMutation.isPending ? (<><Spinner size="sm" className='me-2' />Loading...</>) : 'Approve'}
                        </Button>}
                        {secondaryInspectionData?.SecondaryInspectionReport?.reportFileUrl && secondaryInspectionData?.SecondaryInspectionReport?.status === 'pending' && <Button className="w-100 rounded-0" color="danger" style={{ fontWeight: 500 }} onClick={() => { setUpdatesModalOpen(true) }}>
                            Call to Action
                        </Button>}
                        {secondaryInspectionData?.isInspectorPaid === false && secondaryInspectionData?.SecondaryInspectionReport?.status === 'approved' && <Button className="w-100 rounded-0" color="success" style={{ fontWeight: 500 }} onClick={() => { setTransactionModalOpen(true) }}>
                            Make Payment
                        </Button>}
                    </div>
                </CardBody>
            </Collapse>
            {/* Assign Inspector Modal */}
            <AssignInspectorModal
                isOpen={isAssignModalOpen}
                toggle={toggleAssignModal}
                setInspectionId={setSecondaryInspectionId || (() => { })}
                inspectionId={secondaryInspectionId}
            />
            <TransactionDetailsModal isOpen={transactionModalOpen} toggle={() => setTransactionModalOpen(!transactionModalOpen)} inspectionDetails={secondaryInspectionData} />
            <UpdatesRequiredModal
                isOpen={updatesModalOpen}
                toggle={handleCallToActionCancel}
                onConfirm={handleCallToActionConfirm}
            />
        </Card>
    );
};

export default SecondaryInspectionDetailCard;